import React, { useState, useEffect, useCallback, useRef } from 'react';

interface PurchaseItem {
  id: string;
  shares: number;
  country: string;
  timestamp: string;
}

interface CountryMap {
  [code: string]: { name: string; flag: string };
}

type TimeFilter = '24h' | '7d' | '30d' | '3mo' | '6mo';

const TIME_FILTERS: { key: TimeFilter; label: string }[] = [
  { key: '24h', label: '24h' },
  { key: '7d', label: '7 Days' },
  { key: '30d', label: '30 Days' },
  { key: '3mo', label: '3 Months' },
  { key: '6mo', label: '6 Months' },
];

interface PurchasesModalProps {
  isOpen: boolean;
  onClose: () => void;
  countryMap?: CountryMap;
}

export const PurchasesModal: React.FC<PurchasesModalProps> = ({ isOpen, onClose, countryMap: externalMap }) => {
  const [activeFilter, setActiveFilter] = useState<TimeFilter>('7d');
  const [purchases, setPurchases] = useState<PurchaseItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPurchases, setTotalPurchases] = useState(0);
  const countryMapRef = useRef<CountryMap>(externalMap || {});
  const [mapReady, setMapReady] = useState(!!externalMap && Object.keys(externalMap).length > 0);

  // Load own country map if not provided or empty
  useEffect(() => {
    if (mapReady) return;
    (async () => {
      try {
        const res = await fetch('/api/public/phase-data', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ operation: 'get_country_map' }),
        });
        const result = await res.json();
        if (res.ok && result.data?.countries) {
          const map: CountryMap = {};
          result.data.countries.forEach((c: any) => {
            map[c.country_code] = { name: c.country_name, flag: c.flag_emoji || '🌍' };
          });
          countryMapRef.current = map;
        }
      } catch (err) {
        console.error('Error loading country map:', err);
      } finally {
        setMapReady(true);
      }
    })();
  }, [mapReady]);

  // Sync external map if provided
  useEffect(() => {
    if (externalMap && Object.keys(externalMap).length > 0) {
      countryMapRef.current = externalMap;
    }
  }, [externalMap]);

  const fetchPurchases = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`/api/live-activity?since=${activeFilter}&page=${page}&pageSize=25`);
      if (!res.ok) return;
      const result = await res.json();
      if (result.success) {
        setPurchases(result.activities || []);
        if (result.pagination) {
          setTotalPages(result.pagination.totalPages || 1);
          setTotalPurchases(result.pagination.totalPurchases || 0);
        }
      }
    } catch (err) {
      console.error('Error fetching purchases:', err);
    } finally {
      setIsLoading(false);
    }
  }, [activeFilter, page]);

  useEffect(() => {
    if (isOpen && mapReady) fetchPurchases();
  }, [fetchPurchases, isOpen, mapReady]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const getCountry = (code: string) =>
    countryMapRef.current[code] || { name: code, flag: '🌍' };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Calculate summary client-side
  const totalShares = purchases.reduce((sum, p) => sum + p.shares, 0);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'linear-gradient(135deg, #1a2332 0%, #111827 100%)',
          border: '1px solid rgba(245,158,11,0.25)',
          borderRadius: 16, width: '100%', maxWidth: 560,
          maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
        }}
      >
        {/* Modal header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>📊</span>
            <span style={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>Shareholder Activity</span>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8, color: '#fff', width: 32, height: 32, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        {/* Tabs + summary row */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
            {TIME_FILTERS.map(f => (
              <button
                key={f.key}
                onClick={() => { setActiveFilter(f.key); setPage(1); }}
                style={{
                  padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  background: activeFilter === f.key ? '#f59e0b' : 'rgba(255,255,255,0.07)',
                  color: activeFilter === f.key ? '#000' : 'rgba(255,255,255,0.7)',
                  transition: 'all 0.15s',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 8, padding: '8px 12px' }}>
              <div style={{ color: '#f59e0b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total Purchases</div>
              <div style={{ color: '#fff', fontSize: 20, fontWeight: 700 }}>{isLoading ? '—' : totalPurchases.toLocaleString()}</div>
            </div>
            <div style={{ flex: 1, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 8, padding: '8px 12px' }}>
              <div style={{ color: '#10b981', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Shares</div>
              <div style={{ color: '#fff', fontSize: 20, fontWeight: 700 }}>{isLoading ? '—' : totalShares.toLocaleString()}</div>
            </div>
          </div>
        </div>

        {/* Scrollable list */}
        <div className="purchases-scroll-area" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <style>{`
            .purchases-scroll-area::-webkit-scrollbar { width: 8px; }
            .purchases-scroll-area::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); }
            .purchases-scroll-area::-webkit-scrollbar-thumb { background: rgba(245,158,11,0.4); border-radius: 4px; }
            .purchases-scroll-area::-webkit-scrollbar-thumb:hover { background: rgba(245,158,11,0.6); }
          `}</style>
          {isLoading ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ width: 32, height: 32, border: '3px solid rgba(245,158,11,0.25)', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'purchasesSpin 0.8s linear infinite', margin: '0 auto 10px' }} />
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>Loading...</div>
              <style>{`@keyframes purchasesSpin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : purchases.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>
              No purchases found for this period.
            </div>
          ) : (
            purchases.map((p, i) => {
              const country = getCountry(p.country);
              return (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', borderBottom: i < purchases.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                  <span style={{ fontSize: 20, width: 32, textAlign: 'center', flexShrink: 0 }}>{country.flag}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ color: '#fff', fontSize: 14 }}>
                      Someone in {country.name} purchased <span style={{ color: '#f59e0b', fontWeight: 700 }}>{p.shares.toLocaleString()}</span> share{p.shares !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, flexShrink: 0, whiteSpace: 'nowrap' }}>{formatTime(p.timestamp)}</span>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination controls */}
        {totalPages > 1 && (
          <div style={{
            padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
          }}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1 || isLoading}
              style={{
                padding: '6px 16px', borderRadius: 8, border: 'none', cursor: page <= 1 ? 'not-allowed' : 'pointer',
                fontSize: 13, fontWeight: 600,
                background: page <= 1 ? 'rgba(255,255,255,0.04)' : 'rgba(245,158,11,0.15)',
                color: page <= 1 ? 'rgba(255,255,255,0.25)' : '#f59e0b',
                transition: 'all 0.15s',
              }}
            >
              ← Previous
            </button>
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: 600 }}>
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || isLoading}
              style={{
                padding: '6px 16px', borderRadius: 8, border: 'none', cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                fontSize: 13, fontWeight: 600,
                background: page >= totalPages ? 'rgba(255,255,255,0.04)' : 'rgba(245,158,11,0.15)',
                color: page >= totalPages ? 'rgba(255,255,255,0.25)' : '#f59e0b',
                transition: 'all 0.15s',
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchasesModal;